import { PaletteMode, PaletteOptions, ThemeOptions } from "@mui/material/styles";
import { TypographyOptions } from '@mui/material/styles/createTypography';

export const mindsetSegmentColors = [
  "#82E1F2",
  "#70c9f0",
  "#72b0e7",
  "#8294d5",
  "#9476b8",
  "#9e5892",
  "#9c3c65",
];

export const mindsetSegmentPalette = Object.fromEntries(
  mindsetSegmentColors.map((c, i) => [`segment${i}`, { main: c }])
) as Record<`segment${number}`, { main: string }>;

export const baseTheme: ThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  }
}

export const clientThemes: Record<string, ThemeOptions> = {
  core: baseTheme,
  gravity: baseTheme,
  miroma: baseTheme,
  demo: baseTheme,
  imedia: baseTheme,
}

type paletteConfig = {[ClientKey: string]: {[mode in PaletteMode]?: PaletteOptions}}

export const clientPalettes: paletteConfig = {
  core: {
    dark: {
      mode: "dark" as PaletteMode,
      primary: { main: "#82e1f2", light: "#b3edf7", dark: "#00b1cc", contrastText: "#212b3a" },
      secondary: { main: "#F5B49F", light: "#f8d1c4", dark: "#EA673B", contrastText: "#212b3a" },
      info: { main: "#e6f0ff", light: "#eef8ff", dark: "#cad4ec", contrastText: "#212b3a" },
      warning: { main: "#ffe081", light: "#fff8e1", dark: "#ffb200", contrastText: "#212b3a" },
      error: { main: "#fa8996", light: "#ffe8ee", dark: "#df5678", contrastText: "#212b3a" },
      score: { main: "#e6f0ff", light: "#F5B49F", dark: "#fa8996", contrastText: "#212b3a" },
      background: { default: "#1C2636", paper: "#1C2636" },
      divider: "#58616c",
      gradient: ["#354a5f", "#446e89", "#4f96b3", "#59bfdb", "#66ebff"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    },
    light: {
      mode: "light" as PaletteMode,
      primary: { main: "#00b1cc", light: "#82e1f2", dark: "#00656D", contrastText: "#ffffff" },
      secondary: { main: "#EA673B", light: "#F5B49F", dark: "#b54c29", contrastText: "#ffffff" },
      info: { main: "#1C2636", light: "#404956", dark: "#0B1627", contrastText: "#ffffff" },
      warning: { main: "#ffb200", light: "#FFE081", dark: "#ff6e00", contrastText: "#212b3a" },
      error: { main: "#f3002c", light: "#fa8996", dark: "#c30013", contrastText: "#ffffff" },
      score: { main: "#e6f0ff", light: "#EA673B", dark: "#f3002c", contrastText: "#212b3a" },
      background: { default: "#fff", paper: "#F3F5F9" },
      action: { active: "#212b3a" },
      divider: "#BBBCBF",
      gradient: ["#354a5f", "#446e89", "#4f96b3", "#59bfdb", "#66ebff"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    }
  },
  gravity: {
    dark: {
      mode: "dark" as PaletteMode,
      primary: { main: "#cb8af9", light: "#e0b9fa", dark: "#B557F7", contrastText: "#000000" },
      secondary: { main: "#cb8af9", light: "#e0b9fa", dark: "#B557F7", contrastText: "#000000" },
      info: { main: "#e6f0ff", light: "#eef8ff", dark: "#cad4ec", contrastText: "#000000" },
      warning: { main: "#ffe081", light: "#fff8e1", dark: "#ffb200", contrastText: "#000000" },
      error: { main: "#fa8996", light: "#ffe8ee", dark: "#df5678", contrastText: "#000000" },
      score: { main: "#e0e0e0", light: "#cb8af9", dark: "#fa8996", contrastText: "#000000" },
      gradient: ["#f7d7ff", "#d5a5e6", "#b175ce", "#8b45b6", "#61089f"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    },
    light: {
      mode: "light" as PaletteMode,
      primary: { main: "#B557F7", light: "#cb8af9", dark: "#8841B9", contrastText: "#ffffff" },
      secondary: { main: "#B557F7", light: "#cb8af9", dark: "#8841B9", contrastText: "#ffffff" },
      info: { main: "#1C2636", light: "#404956", dark: "#0B1627", contrastText: "#ffffff" },
      warning: { main: "#ffb200", light: "#FFE081", dark: "#ff6e00", contrastText: "#212b3a" },
      error: { main: "#f3002c", light: "#fa8996", dark: "#c30013", contrastText: "#ffffff" },
      score: { main: "#e0e0e0", light: "#B557F7", dark: "#f3002c", contrastText: "#000000" },
      background: { default: "#fff", paper: "#F3F5F9" },
      action: { active: "#212b3a" },
      divider: "#BBBCBF",
      gradient: ["#f7d7ff", "#d5a5e6", "#b175ce", "#8b45b6", "#61089f"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    }
  },
  miroma: {
    dark: {
      mode: "dark" as PaletteMode,
      primary: { main: "#FFFFFF", light: "#FFFFFF", dark: "#f2f2f2", contrastText: "#000000" },
      secondary: { main: "#3185FC", light: "#91caff", dark: "#3261d5", contrastText: "#FFFFFF" },
      info: { main: "#3185FC", light: "#91caff", dark: "#3261d5", contrastText: "#FFFFFF" },
      warning: { main: "#ffe081", light: "#fff8e1", dark: "#ffb200", contrastText: "#000000" },
      error: { main: "#fa8996", light: "#ffe8ee", dark: "#df5678", contrastText: "#000000" },
      score: { main: "#FFFFFF", light: "#3185FC", dark: "#fa8996", contrastText: "#000000" },
      divider: "#ffffff3b",
      gradient: ["#E8F2FF", "#A6CBFF", "#3185FC", "#1A63CB", "#0D3B7A"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    },
    light: {
      mode: "light" as PaletteMode,
      primary: { main: "#000000", light: "#434343", dark: "#000000", contrastText: "#FFFFFF" },
      secondary: { main: "#3185FC", light: "#91caff", dark: "#3261d5", contrastText: "#FFFFFF" },
      info: { main: "#3185FC", light: "#91caff", dark: "#3261d5", contrastText: "#FFFFFF" },
      warning: { main: "#ffe081", light: "#fff8e1", dark: "#ffb200", contrastText: "#000000" },
      error: { main: "#fa8996", light: "#ffe8ee", dark: "#df5678", contrastText: "#000000" },
      score: { main: "#FFFFFF", light: "#3185FC", dark: "#f3002c", contrastText: "#000000" },
      background: { default: "#fff", paper: "#e0e0e0" },
      action: { active: "#000000", },
      divider: "#0000003b",
      gradient: ["#E8F2FF", "#A6CBFF", "#3185FC", "#1A63CB", "#0D3B7A"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    }
  },
  demo: {
    dark: {
      mode: "dark" as PaletteMode,
      primary: { main: "#e6f0ff", light: "#eef8ff", dark: "#cad4ec", contrastText: "#000000" },
      secondary: { main: "#e6f0ff", light: "#eef8ff", dark: "#cad4ec", contrastText: "##000000" },
      info: { main: "#e6f0ff", light: "#eef8ff", dark: "#cad4ec", contrastText: "#000000" },
      warning: { main: "#ffe081", light: "#fff8e1", dark: "#ffb200", contrastText: "#000000" },
      error: { main: "#fa8996", light: "#ffe8ee", dark: "#df5678", contrastText: "#000000" },
      score: { main: "#e0e0e0", light: "#cad4ec", dark: "#fa8996", contrastText: "#000000" },
      gradient: ["#f7d7ff", "#d5a5e6", "#b175ce", "#8b45b6", "#61089f"],
    }
  },
  imedia: {
    dark: {
      mode: "dark" as PaletteMode,
      cta: { main: "#E60085", light: "#ed83ba", dark: "#be0065", contrastText: "#FFFFFF" },
      primary: { main: "#E60085", light: "#ed83ba", dark: "#be0065", contrastText: "#FFFFFF" },
      secondary: { main: "#ed83ba", light: "#fae2ee", dark: "#ed4ca9", contrastText: "#454554" },
      info: { main: "#d9d9dc", light: "#ececee", dark: "#b4b4ba", contrastText: "#454554" },
      warning: { main: "#ffe081", light: "#fff8e1", dark: "#ffb200", contrastText: "#000000" },
      error: { main: "#fa8996", light: "#ffe8ee", dark: "#df5678", contrastText: "#000000" },
      score: { main: "#e6f0ff", light: "#ed83ba", dark: "#797889", contrastText: "#000000" },
      background: { default: "#151519", paper: "#30303B" },
      divider: "#23232A",
      gradient: ["#473f5c", "#674878", "#8e4d8e", "#ba4e9a", "#e84c9d"],
      segments: mindsetSegmentColors,
      ...mindsetSegmentPalette,
    }
  }
}

export const clientFonts: Record<string, TypographyOptions> = {
  core: {
    fontFamily: "cera-pro",
  },
  gravity: {
    fontFamily: "cera-pro",
  },
  miroma: {
    fontFamily: "miroma",
  },
  demo: {
    fontFamily: "cera-pro",
  },
  imedia: {
    fontFamily: "poppins",
  }
}



/*
  Core Connect
 */

export const coreDarkTheme = {
  palette: {
    mode: "dark" as PaletteMode,
    primary: {
      main: "#82e1f2",
      light: "#b3edf7",
      dark: "#00b1cc",
      contrastText: "#212b3a",
    },
    secondary: {
      main: "#F5B49F",
      light: "#f8d1c4",
      dark: "#EA673B",
      contrastText: "#212b3a",
    },
    info: {
      main: "#e6f0ff",
      light: "#eef8ff",
      dark: "#cad4ec",
      contrastText: "#212b3a",
    },
    warning: {
      main: "#ffe081",
      light: "#fff8e1",
      dark: "#ffb200",
      contrastText: "#212b3a",
    },
    error: {
      main: "#fa8996",
      light: "#ffe8ee",
      dark: "#df5678",
      contrastText: "#212b3a",
    },
    background: {
      default: "#1C2636",
      paper: "#1C2636",
    },
    divider: "#58616c",
    gradient: [
      "#354a5f",
      "#446e89",
      "#4f96b3",
      "#59bfdb",
      "#66ebff"
    ],
    score: {
      main: "#e6f0ff",
      light: "#F5B49F",
      dark: "#fa8996",
      contrastText: "#212b3a",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "cera-pro",
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}

export const coreLightTheme = {
  palette: {
    mode: "light" as PaletteMode,
    primary: {
      main: "#00b1cc",
      light: "#82e1f2",
      dark: "#00656D",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#EA673B",
      light: "#F5B49F",
      dark: "#b54c29",
      contrastText: "#ffffff",
    },
    info: {
      main: "#1C2636",
      light: "#404956",
      dark: "#0B1627",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ffb200",
      light: "#FFE081",
      dark: "#ff6e00",
      contrastText: "#212b3a",
    },
    error: {
      main: "#f3002c",
      light: "#fa8996",
      dark: "#c30013",
      contrastText: "#ffffff",
    },
    background: {
      default: "#fff",
      paper: "#F3F5F9",
    },
    action: {
      active: "#212b3a",
    },
    divider: "#BBBCBF",
    gradient: [
      "#354a5f",
      "#446e89",
      "#4f96b3",
      "#59bfdb",
      "#66ebff"
    ],
    score: {
      main: "#e6f0ff",
      light: "#EA673B",
      dark: "#f3002c",
      contrastText: "#212b3a",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "cera-pro",
    allVariants: {
      color: "#212b3a",
    },
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}


/*
  Gravity Connect
 */

export const gravityDarkTheme = {
  palette: {
    mode: "dark" as PaletteMode,
    primary: {
      main: "#cb8af9",
      light: "#e0b9fa",
      dark: "#B557F7",
      contrastText: "#000000",
    },
    secondary: {
      main: "#cb8af9",
      light: "#e0b9fa",
      dark: "#B557F7",
      contrastText: "#000000",
    },
    info: {
      main: "#e6f0ff",
      light: "#eef8ff",
      dark: "#cad4ec",
      contrastText: "#000000",
    },
    warning: {
      main: "#ffe081",
      light: "#fff8e1",
      dark: "#ffb200",
      contrastText: "#000000",
    },
    error: {
      main: "#fa8996",
      light: "#ffe8ee",
      dark: "#df5678",
      contrastText: "#000000",
    },
    gradient: [
      "#f7d7ff",
      "#d5a5e6",
      "#b175ce",
      "#8b45b6",
      "#61089f"
    ],
    score: {
      main: "#e0e0e0",
      light: "#cb8af9",
      dark: "#fa8996",
      contrastText: "#000000",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "cera-pro",
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}

export const gravityLightTheme = {
  palette: {
    mode: "light" as PaletteMode,
    primary: {
      main: "#B557F7",
      light: "#cb8af9",
      dark: "#8841B9",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#B557F7",
      light: "#cb8af9",
      dark: "#8841B9",
      contrastText: "#ffffff",
    },
    info: {
      main: "#1C2636",
      light: "#404956",
      dark: "#0B1627",
      contrastText: "#ffffff",
    },
    warning: {
      main: "#ffb200",
      light: "#FFE081",
      dark: "#ff6e00",
      contrastText: "#212b3a",
    },
    error: {
      main: "#f3002c",
      light: "#fa8996",
      dark: "#c30013",
      contrastText: "#ffffff",
    },
    background: {
      default: "#fff",
      paper: "#F3F5F9",
    },
    action: {
      active: "#212b3a",
    },
    divider: "#BBBCBF",
    gradient: [
      "#f7d7ff",
      "#d5a5e6",
      "#b175ce",
      "#8b45b6",
      "#61089f"
    ],
    score: {
      main: "#e0e0e0",
      light: "#B557F7",
      dark: "#f3002c",
      contrastText: "#000000",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "cera-pro",
    allVariants: {
      color: "#212b3a",
    },
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}


/*
  Miroma Connect
 */

export const miromaDarkTheme = {
  palette: {
    mode: "dark" as PaletteMode,
    primary: {
      main: "#FFFFFF",
      light: "#FFFFFF",
      dark: "#f2f2f2",
      contrastText: "#000000",
    },
    secondary: {
      main: "#3185FC",
      light: "#91caff",
      dark: "#3261d5",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#3185FC",
      light: "#91caff",
      dark: "#3261d5",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#ffe081",
      light: "#fff8e1",
      dark: "#ffb200",
      contrastText: "#000000",
    },
    error: {
      main: "#fa8996",
      light: "#ffe8ee",
      dark: "#df5678",
      contrastText: "#000000",
    },
    divider: "#ffffff3b",
    gradient: [
      "#E8F2FF",
      "#A6CBFF",
      "#3185FC",
      "#1A63CB",
      "#0D3B7A"
    ],
    score: {
      main: "#FFFFFF",
      light: "#3185FC",
      dark: "#fa8996",
      contrastText: "#000000",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "miroma",
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}

export const miromaLightTheme = {
  palette: {
    mode: "light" as PaletteMode,
    primary: {
      main: "#000000",
      light: "#434343",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#3185FC",
      light: "#91caff",
      dark: "#3261d5",
      contrastText: "#FFFFFF",
    },
    info: {
      main: "#3185FC",
      light: "#91caff",
      dark: "#3261d5",
      contrastText: "#FFFFFF",
    },
    warning: {
      main: "#ffb200",
      light: "#FFE081",
      dark: "#ff6e00",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#f3002c",
      light: "#fa8996",
      dark: "#c30013",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#fff",
      paper: "#e0e0e0",
    },
    action: {
      active: "#000000",
    },
    divider: "#0000003b",
    gradient: [
      "#E8F2FF",
      "#A6CBFF",
      "#3185FC",
      "#1A63CB",
      "#0D3B7A"
    ],
    score: {
      main: "#FFFFFF",
      light: "#3185FC",
      dark: "#f3002c",
      contrastText: "#000000",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "miroma",
    // allVariants: {
    //   color: "#000000",
    // },
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}

/*
  Miroma Connect
 */

export const demoTheme = {
  palette: {
    mode: "dark" as PaletteMode,
    primary: {
      main: "#e6f0ff",
      light: "#eef8ff",
      dark: "#cad4ec",
      contrastText: "#000000",
    },
    secondary: {
      main: "#e6f0ff",
      light: "#eef8ff",
      dark: "#cad4ec",
      contrastText: "#000000",
    },
    info: {
      main: "#e6f0ff",
      light: "#eef8ff",
      dark: "#cad4ec",
      contrastText: "#000000",
    },
    warning: {
      main: "#ffe081",
      light: "#fff8e1",
      dark: "#ffb200",
      contrastText: "#000000",
    },
    error: {
      main: "#fa8996",
      light: "#ffe8ee",
      dark: "#df5678",
      contrastText: "#000000",
    },
    gradient: [
      "#f7d7ff",
      "#d5a5e6",
      "#b175ce",
      "#8b45b6",
      "#61089f"
    ],
    score: {
      main: "#e0e0e0",
      light: "#cad4ec",
      dark: "#fa8996",
      contrastText: "#000000",
    },
    segments: mindsetSegmentColors,
    ...mindsetSegmentPalette,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 754,
      md: 1028,
      lg: 1200,
      xl: 1448,
    }
  },
  typography: {
    fontFamily: "cera-pro",
  },
  components: {
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: "4px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
        },
      },
    },
  },
}
